import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import {
  forgotPassword,
  verifyOTP,
  resendOTP,
  resetForgotPasswordState,
  resetVerifyOTPState,
  selectForgotPassword,
  selectVerifyOTP,
  selectResendOTP,
} from "../../redux/slices/authSlice";
import { toast } from "react-toastify";
import PageTitle from "../../components/PageTitle";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const forgotPasswordState = useSelector(selectForgotPassword);
  const verifyOTPState = useSelector(selectVerifyOTP);
  const resendOTPState = useSelector(selectResendOTP);

  // Local State
  const [step, setStep] = useState(1); // 1: Email, 2: OTP
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");

  // Reset OTP states on unmount
  useEffect(() => {
    return () => {
      dispatch(resetForgotPasswordState());
      dispatch(resetVerifyOTPState());
    };
  }, [dispatch]);

  // Handle Forgot Password (Send OTP)
  // const handleSendOTP = async (e) => {
  //   e.preventDefault();
  //   if (!email) {
  //     setOtpError("Please enter your email address");
  //     return;
  //   }
  //   setOtpError("");
  //   await dispatch(forgotPassword(email));
  // };

  const handleSendOTP = async (e) => {
  e.preventDefault();

  if (!email) {
    toast.error("Please enter your email address");
    return;
  }

  const result = await dispatch(forgotPassword(email));

  if (result.type.includes("fulfilled")) {
    // toast.success("OTP sent successfully");
  } else {
    toast.error(result.payload || "Failed to send OTP");
  }
};

  // Handle Verify OTP
  // const handleVerifyOTP = async (e) => {
  //   e.preventDefault();
  //   if (!otp || otp.length !== 6) {
  //     setOtpError("Please enter a valid 6-digit OTP");
  //     return;
  //   }
  //   setOtpError("");
  //   const result = await dispatch(verifyOTP({ email, otp }));
    
  //   // If OTP verified successfully, redirect to Reset Password page with token
  //   if (result.payload?.success) {
  //     const resetToken = result.payload?.data?.resetToken;
  //     if (resetToken) {
  //       navigate(`/reset-password/${resetToken}`);
  //     }
  //   }
  // };

  const handleVerifyOTP = async (e) => {
  e.preventDefault();

  if (!otp || otp.length !== 6) {
    toast.error("Please enter a valid 6-digit OTP");
    return;
  }

  const result = await dispatch(verifyOTP({ email, otp }));

  if (result.payload?.success) {
    toast.success("OTP verified successfully");

    const resetToken = result.payload?.data?.resetToken;

    if (resetToken) {
      navigate(`/reset-password/${resetToken}`);
    }
  } else {
    toast.error(result.payload || "Invalid OTP");
  }
};
  // // Handle Resend OTP
  // const handleResendOTP = () => {
  //   dispatch(resendOTP(email));
  // };

  const handleResendOTP = async () => {
  const result = await dispatch(resendOTP(email));

  if (result.type.includes("fulfilled")) {
    toast.success("OTP resent successfully");
  } else {
    toast.error(result.payload || "Failed to resend OTP");
  }
};

  // Reset to step 1
  const handleBackToEmail = () => {
    setStep(1);
    dispatch(resetForgotPasswordState());
    dispatch(resetVerifyOTPState());
    setOtp("");
    setOtpError("");
  };

  // Check if OTP was sent successfully
  // useEffect(() => {
  //   if (forgotPasswordState.success) {
  //     setStep(2);
  //   }
  // }, [forgotPasswordState.success]);

  useEffect(() => {
  if (forgotPasswordState.success) {
    toast.success("OTP sent to your email");
    setStep(2);
  }
}, [forgotPasswordState.success]);

  return (
    <>
       <PageTitle
  title="Forgot Password – RestaurantWala"
  description="Reset your RestaurantWala account password securely and regain access to your restaurant management dashboard."
  keywords="RestaurantWala forgot password, reset restaurant software password, restaurant management login"
/>
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--primary-light) 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        py: 4,
      }}
    >
      {/* Back to Home Button */}
      <Box
        sx={{
          position: "absolute",
          top: 24,
          left: 24,
          zIndex: 10,
        }}
      >
        <Button
          component={Link}
          to="/"
          startIcon={<ArrowBackIcon />}
          sx={{
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "50px",
            px: 2.5,
            py: 1,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9rem",
            border: "1px solid rgba(255,255,255,0.2)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.25)",
              transform: "scale(1.05)",
            },
            transition: "all 0.3s ease",
          }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
          Back to Home
        </Button>
      </Box>

      {/* Decorative Background Elements */}
      {[
        { size: 300, top: "-10%", left: "-5%", opacity: 0.08 },
        { size: 400, bottom: "-15%", right: "-8%", opacity: 0.08 },
        { size: 200, top: "30%", right: "10%", opacity: 0.06 },
      ].map((circle, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: circle.size,
            height: circle.size,
            borderRadius: "50%",
            bgcolor: "white",
            opacity: circle.opacity,
            top: circle.top,
            left: circle.left,
            bottom: circle.bottom,
            right: circle.right,
          }}
        />
      ))}

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 4,
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }}
        >
          {/* Logo & Title */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2.5,
                boxShadow: "var(--shadow-orange-hover)",
              }}
            >
              {step === 2 ? (
                <EmailIcon sx={{ fontSize: 44, color: "white" }} />
              ) : (
                <EmailIcon sx={{ fontSize: 44, color: "white" }} />
              )}
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "1.5rem", sm: "1.9rem" },
                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 0.5,
              }}
            >
              {step === 2 ? "Verify OTP" : "Forgot Password"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "var(--text-light)", fontSize: "0.95rem" }}
            >
              {step === 2
                ? `Enter the 6-digit OTP sent to ${email}`
                : "Enter your email to reset your password"}
            </Typography>
          </Box>

          {/* Error Alert */}
          {/* {(forgotPasswordState.error || verifyOTPState.error || otpError) && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  color: "var(--primary)",
                },
              }}
            >
              {forgotPasswordState.error || verifyOTPState.error || otpError}
            </Alert>
          )} */}

          {/* Step 1: Email */}
          {step === 1 && (
            <Box component="form" onSubmit={handleSendOTP}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={forgotPasswordState.loading}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "var(--primary)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--primary)",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--primary)",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "var(--primary)" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={forgotPasswordState.loading || !email}
                endIcon={
                  forgotPasswordState.loading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    <SendIcon />
                  )
                }
                sx={{
                  py: 1.5,
                  background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                    transform: "translateY(-3px)",
                    boxShadow: "var(--shadow-orange-hover)",
                  },
                  "&:disabled": {
                    background: "var(--border)",
                    color: "var(--text-light)",
                  },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "1rem",
                  boxShadow: "var(--shadow-orange)",
                  transition: "all 0.3s ease",
                }}
              >
                {forgotPasswordState.loading ? "Sending..." : "Send OTP"}
              </Button>
            </Box>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <Box component="form" onSubmit={handleVerifyOTP}>
              <TextField
                fullWidth
                label="Enter OTP"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                disabled={verifyOTPState.loading}
                placeholder="Enter 6-digit OTP"
                inputProps={{
                  maxLength: 6,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "var(--primary)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--primary)",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--primary)",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "var(--primary)" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={verifyOTPState.loading || otp.length !== 6}
                endIcon={
                  verifyOTPState.loading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                sx={{
                  py: 1.5,
                  background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                    transform: "translateY(-3px)",
                    boxShadow: "var(--shadow-orange-hover)",
                  },
                  "&:disabled": {
                    background: "var(--border)",
                    color: "var(--text-light)",
                  },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "1rem",
                  boxShadow: "var(--shadow-orange)",
                  transition: "all 0.3s ease",
                }}
              >
                {verifyOTPState.loading ? "Verifying..." : "Verify OTP"}
              </Button>

              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                <Button
                  variant="text"
                  onClick={handleBackToEmail}
                  sx={{
                    color: "var(--text-light)",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      color: "var(--primary)",
                    },
                  }}
                >
                  ← Back
                </Button>
                <Button
                  variant="text"
                  onClick={handleResendOTP}
                  disabled={resendOTPState.loading}
                  sx={{
                    color: "var(--primary)",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      color: "var(--primary-dark)",
                    },
                  }}
                >
                  {resendOTPState.loading ? "Sending..." : "Resend OTP"}
                </Button>
              </Box>
            </Box>
          )}

          {/* Divider */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              my: 3,
            }}
          >
            <Box sx={{ flex: 1, height: 1, bgcolor: "var(--border)" }} />
            <Typography variant="caption" sx={{ color: "var(--text-light)" }}>
              OR
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: "var(--border)" }} />
          </Box>

          {/* Back to Login */}
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "var(--text-light)" }}>
              Remember your password?{" "}
              <Link
                to="/login"
                style={{
                  color: "var(--primary)",
                  textDecoration: "none",
                  fontWeight: 700,
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = "var(--primary-dark)";
                  e.target.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = "var(--primary)";
                  e.target.style.textDecoration = "none";
                }}
              >
                Back to Login
              </Link>
            </Typography>
          </Box>
        </Paper>

        {/* Footer Text */}
       
      </Container>
       <Typography
          variant="caption"
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  color: "rgba(255,255,255,0.75)",
                  fontSize: 11,
                  fontWeight: 500,
                  zIndex: 1,
                }}
        >
          © {new Date().getFullYear()} RestaurantWala • Your Digital Restaurant Partner
        </Typography>
    </Box>
    </>
  );
};

export default ForgotPassword;